import { useCallback, useEffect, useRef, useState } from 'react'
import { Link } from 'gatsby'
import { InView } from 'react-intersection-observer'
import { Picture } from 'src/components/ui/Image'
import ArrowLeftClub from 'src/components/Icons/ArrowLeftClub'

import type { PictoCardInteractionHandlers, PictoItem } from './Pictos'

type Props = {
  items: PictoItem[]
  titleSection?: string
} & PictoCardInteractionHandlers

export function PictosDesktop({
  items,
  titleSection,
  onCardClick,
  onCardInView,
}: Props) {
  const listRef = useRef<HTMLUListElement>(null)
  const [hasDesktopOverflow, setHasDesktopOverflow] = useState(false)
  const [canScrollPrev, setCanScrollPrev] = useState(false)
  const [canScrollNext, setCanScrollNext] = useState(false)

  const updateDesktopArrowsState = useCallback(() => {
    if (!listRef.current) {
      setHasDesktopOverflow(false)
      setCanScrollPrev(false)
      setCanScrollNext(false)

      return
    }

    const { scrollLeft, scrollWidth, clientWidth } = listRef.current
    const maxScrollLeft = scrollWidth - clientWidth
    const hasHorizontalOverflow = maxScrollLeft > 2

    setHasDesktopOverflow(hasHorizontalOverflow)

    if (!hasHorizontalOverflow) {
      setCanScrollPrev(false)
      setCanScrollNext(false)

      return
    }

    setCanScrollPrev(scrollLeft > 2)
    setCanScrollNext(scrollLeft < maxScrollLeft - 2)
  }, [])

  useEffect(() => {
    updateDesktopArrowsState()

    if (!window) {
      return
    }

    const onResize = () => {
      updateDesktopArrowsState()
    }

    window.addEventListener('resize', onResize)

    return () => {
      window.removeEventListener('resize', onResize)
    }
  }, [items, updateDesktopArrowsState])

  const handleScroll = (direction: 'previous' | 'next') => {
    if (
      !listRef.current ||
      (direction === 'previous' && !canScrollPrev) ||
      (direction === 'next' && !canScrollNext)
    ) {
      return
    }

    const scrollDistance = 340

    listRef.current.scrollBy({
      left: direction === 'next' ? scrollDistance : -scrollDistance,
      behavior: 'smooth',
    })
  }

  const prevArrowClass = canScrollPrev
    ? 'text-restructure-primary'
    : 'text-restructure-disabled'

  const nextArrowClass = canScrollNext
    ? 'text-restructure-primary'
    : 'text-restructure-disabled'

  return (
    <section aria-label={titleSection}>
      <div className="mb-[22px] flex items-center justify-between gap-4 intermediate:px-[42px]">
        <h2 className="text-left font-inter text-[24px] leading-[30px] sm:text-[26px] sm:leading-[39px] font-semibold text-restructure-primary">
          {titleSection}
        </h2>

        {hasDesktopOverflow ? (
          <div className="hidden restructure-small-desktop:flex gap-6 items-center">
            <button
              type="button"
              className={`w-10 h-10 p-0 rounded-full border border-restructure-border-secondary bg-white flex items-center justify-center transition-colors hover:border-restructure-border-hover disabled:cursor-default disabled:border-restructure-border-secondary disabled:hover:border-restructure-border-secondary ${prevArrowClass}`}
              onClick={() => handleScroll('previous')}
              aria-label="Ver cards anteriores"
              disabled={!canScrollPrev}
            >
              <span
                className="inline-flex items-center justify-center w-4 h-4 leading-none"
                aria-hidden="true"
              >
                <ArrowLeftClub color="currentColor" />
              </span>
            </button>

            <button
              type="button"
              className={`w-10 h-10 p-0 rounded-full border border-restructure-border-secondary bg-white flex items-center justify-center transition-colors hover:border-restructure-border-hover disabled:cursor-default disabled:border-restructure-border-secondary disabled:hover:border-restructure-border-secondary ${nextArrowClass}`}
              onClick={() => handleScroll('next')}
              aria-label="Ver próximos cards"
              disabled={!canScrollNext}
            >
              <span
                className="inline-flex items-center justify-center w-4 h-4 leading-none rotate-180"
                aria-hidden="true"
              >
                <ArrowLeftClub color="currentColor" />
              </span>
            </button>
          </div>
        ) : null}
      </div>

      <ul
        ref={listRef}
        onScroll={updateDesktopArrowsState}
        className={`${
          items.length <= 4 ? 'lg:justify-center' : ''
        } flex items-start justify-start max-w-full overflow-x-scroll scrollbar-hide gap-3 sm:gap-[13.82px] intermediate:mx-[42px]`}
      >
        {items.map((item, index) => (
          <InView
            as="li"
            threshold={0.7}
            onChange={(inView) => {
              if (inView) {
                onCardInView(item, index)
              }
            }}
            triggerOnce
            key={`${item.text}-${index}`}
            draggable={false}
            data-testid="picto-card"
            className="flex-shrink-0 w-[271px] restructure-small-desktop:w-[325.4px]"
          >
            <Link
              to={item.linkUrl}
              className="relative block w-full h-[170px] restructure-small-desktop:h-[205px] overflow-hidden bg-restructure-background-secondary"
              onClick={() => onCardClick(item, index)}
              aria-label={`link para a categoria ${item.text}`}
            >
              <span className="absolute inset-0">
                <Picture
                  className="block h-full w-full object-cover"
                  sources={[
                    {
                      src: item.image,
                      width: 325,
                      height: 205,
                      media: '(min-width: 767px)',
                    },
                  ]}
                  img={{
                    src: item.image,
                    width: 325,
                    height: 205,
                    'data-id': item.image,
                    title: item.text,
                    alt: item.altImage,
                    loading: 'eager',
                  }}
                  shouldCompress={false}
                />
                <span className="absolute inset-x-0 top-[44px] bottom-0 bg-gradient-to-t from-black via-[rgba(0,0,0,0.35)] via-1/2 to-[rgba(0,0,0,0)]" />
              </span>
              <h2
                data-testid="picto-text"
                className="absolute left-[13px] right-[13px] bottom-[54px] m-0 z-[1] text-restructure-primary-inverted text-left pointer-events-none font-inter font-semibold text-[18px] leading-5 restructure-small-desktop:text-[20px]"
              >
                {item.text}
              </h2>
              <h2
                data-testid="picto-subtext"
                className="absolute h-[30px] left-[13px] right-[13px] bottom-4 m-0 z-[1] text-left pointer-events-none font-inter font-normal text-[13px] leading-[1.25] text-[rgba(255,255,255,0.85)] line-clamp-2"
              >
                {item.subtext}
              </h2>
            </Link>
          </InView>
        ))}
      </ul>
    </section>
  )
}
